import * as React from 'react';
import styled from 'styled-components';
import { debounce } from '@agilepoint/utils-common';
import { IToolbarItemProps, IToolbarRendererProps } from '../types';
import { ToolbarItem } from '../toolbar-item';
import { Callout } from '../../../callout';
import { IconSize } from '../../../icon';
import { useWindowSizeContext } from '../../../../utils';
import { selectThemeSemantics } from '../../../../styling/theming';
import { StackPanel } from '../../../stack-panel';
import { FlexBox } from '../../../flex-box';

const SecondaryToolbarItem = styled(ToolbarItem)`
    width: 100%;
    height: auto;
    padding: ${(selectThemeSemantics(({ gap }) => `${gap.l} ${gap.xl}`))};
`;

const MoreActionButton = styled(ToolbarItem).attrs({
    className: String('ap-toolbar-moreActions')
})`
    display: flex;
`;


const PrimaryToolbarItemsWrapper = styled(StackPanel).attrs({
    className: String('ap-toolbar-primaryItemsWrapper')
})`
    display: flex;
    flex-shrink: 0;
    flex-grow: 0;
    height: 100%;
`;

const StyledCallout = styled(Callout).attrs({
    className: String('ap-toolbar-secondaryActionsWrapper')
})`
    z-index: 4;
`;

function useCommandFilter(commands: IToolbarItemProps[], maxPrimaryItems?: number) {
    const filterReducer = React.useCallback((result, command) => {
        const { primaryCommands, secondaryCommands } = result;
        if (maxPrimaryItems === undefined || (primaryCommands.length < maxPrimaryItems)) {
            primaryCommands.push(command);
        } else {
            secondaryCommands.push(command);
        }
        return result;
    }, [maxPrimaryItems]);

    return React.useMemo(() => commands.reduce(filterReducer, {
        primaryCommands: [] as IToolbarItemProps[],
        secondaryCommands: [] as IToolbarItemProps[]
    }), [commands, filterReducer]);
}

interface IUseRenderMoreButtonOptions {
    iconSize: IconSize;
    windowWidth: number;
}

type FunctionWithoutArguments = () => void;

function useVisibility(initialState: boolean) {
    const [isVisible, dispatch] = React.useReducer((visibilityState: boolean, action: 'TOGGLE' | 'RESET') => (action === 'TOGGLE' ? !visibilityState : initialState), initialState);

    const toggleVisibility = React.useCallback(() => dispatch('TOGGLE'), []);
    const resetVisibility = React.useCallback(() => dispatch('RESET'), []);

    return [isVisible, toggleVisibility, resetVisibility] as [boolean, FunctionWithoutArguments, FunctionWithoutArguments];
}

function useRenderMoreButton(options: IUseRenderMoreButtonOptions) {
    const { iconSize, windowWidth } = options;
    const moreButtonRef = React.useRef(null);
    const [showSecondaryCommands, toggleSecondaryCommandsVisibility, resetSecondaryCommandsVisibility] = useVisibility(false);

    const _onRenderScondaryCommand = React.useCallback((itemProps: IToolbarItemProps, index: number) => (
        <SecondaryToolbarItem iconSize={iconSize} windowWidth={windowWidth} onItemInteracted={resetSecondaryCommandsVisibility} {...itemProps} key={itemProps.id} itemIndex={index} />
    ), [iconSize, resetSecondaryCommandsVisibility, windowWidth]);

    return React.useCallback((secondaryCommands: IToolbarItemProps[]) => (
        <React.Fragment>
            <MoreActionButton
                key="ap-more-button"
                id="ap-more-button"
                iconName="ap-menu-round.outline"
                elementRef={moreButtonRef}
                onClick={toggleSecondaryCommandsVisibility}
                itemIndex={-1}
                iconSize={iconSize}
                windowWidth={windowWidth} />
            <StyledCallout
                closeOnEscape={true}
                autofocusOnOpen={true}
                targetRef={moreButtonRef}
                onDismiss={resetSecondaryCommandsVisibility}
                hidden={!showSecondaryCommands}>
                <FlexBox flexDirection="column">
                    {secondaryCommands.map(_onRenderScondaryCommand)}
                </FlexBox>
            </StyledCallout>
        </React.Fragment>
    ), [showSecondaryCommands, iconSize, windowWidth, toggleSecondaryCommandsVisibility, resetSecondaryCommandsVisibility, _onRenderScondaryCommand]);
}

function useMaxPrimaryItems(commands: IToolbarItemProps[], initialMaxPrimaryItems?: number, windowWidth?: number) {
    const [maxItems, setMaxPrimaryItems] = React.useState(initialMaxPrimaryItems);
    const prevWindowWidth = React.useRef(windowWidth);
    const prevCommands = React.useRef(commands);

    const setMaxPrimaryItemsDebounced = React.useMemo(() => debounce(setMaxPrimaryItems, 100, null), []);

    const maxPrimaryItems = React.useMemo(() => {
        let shouldRecalculateMaxItems = prevWindowWidth.current !== windowWidth;
        if (commands.length !== prevCommands.current.length) {
            prevCommands.current = commands;
            shouldRecalculateMaxItems = true;
        }
        if (shouldRecalculateMaxItems) {
            prevWindowWidth.current = windowWidth;
            if (initialMaxPrimaryItems) {
                setMaxPrimaryItems(initialMaxPrimaryItems);
                return initialMaxPrimaryItems;
            }
            setMaxPrimaryItems(undefined);
        }
        return maxItems;
    }, [windowWidth, maxItems, commands, initialMaxPrimaryItems]);

    return [maxPrimaryItems, setMaxPrimaryItemsDebounced] as [number, (maxItems?: number) => void];
}

function concatIfFarPositionedToolbarItemsAvailable(farPositionedToolbarItems: IToolbarItemProps[], secondaryCommands: IToolbarItemProps[]) {
    if (farPositionedToolbarItems.length > 0) {
        secondaryCommands = [...secondaryCommands, ...farPositionedToolbarItems];
    }
    return secondaryCommands;
}

export const ToolbarOverflowMenu = React.memo((props: IToolbarRendererProps) => {
    const { primaryToolbarItems, containerRef, maxPrimaryItems: maxcount, farPositionedToolbarItems, iconSize, iconPosition, separator, separatorGap, height } = props;
    const { windowWidth } = useWindowSizeContext();
    const [maxPrimaryItems, setMaxPrimaryItems] = useMaxPrimaryItems(primaryToolbarItems, maxcount, windowWidth);
    const { primaryCommands, secondaryCommands } = useCommandFilter(primaryToolbarItems, maxPrimaryItems);
    const secondaryCommandsConcated = React.useMemo(() => concatIfFarPositionedToolbarItemsAvailable(farPositionedToolbarItems, secondaryCommands), [farPositionedToolbarItems, secondaryCommands]);
    const _onItemLoad = React.useCallback((itemPosition: Pick<DOMRect, 'left' | 'width'>, itemIndex: number) => {
        const { current } = containerRef;

        if (!current) {
            return;
        }

        const toolbarWidth = current.offsetWidth;
        const optimumPrimaryItemsSpace = toolbarWidth - 100;
        const toolbarLeftPosition = current.offsetLeft;
        const left = itemPosition.left - toolbarLeftPosition;
        const isStartPositionInViewArea = optimumPrimaryItemsSpace > left;
        const isEndPositionInViewArea = optimumPrimaryItemsSpace > (left + itemPosition.width);

        if (isStartPositionInViewArea && isEndPositionInViewArea) {
            setMaxPrimaryItems(itemIndex + 1);
        } else if (isStartPositionInViewArea && !isEndPositionInViewArea) {
            setMaxPrimaryItems(itemIndex);
        }
    }, [containerRef, setMaxPrimaryItems]);

    const renderMoreButton = useRenderMoreButton({ iconSize, windowWidth });

    const _onRenderPrimaryCommand = React.useCallback((itemProps: IToolbarItemProps, index: number) => (
        <ToolbarItem iconPosition={iconPosition} iconSize={iconSize} windowWidth={windowWidth} {...itemProps} key={itemProps.id} itemIndex={index} onItemLoad={_onItemLoad} height={height} />
    ), [_onItemLoad, iconSize, windowWidth, iconPosition, height]);

    return (
        <React.Fragment>
            <PrimaryToolbarItemsWrapper gap={separatorGap} separator={separator} alignment="center">
                {primaryCommands.map(_onRenderPrimaryCommand)}
            </PrimaryToolbarItemsWrapper>
            {secondaryCommandsConcated.length ? renderMoreButton(secondaryCommandsConcated) : null}
        </React.Fragment>
    );
});

ToolbarOverflowMenu.displayName = 'ToolbarOverflowMenu';
